import { Link, Navigate } from "react-router-dom";
import { Seo, breadcrumbLd } from "../lib/seo";
import { MaskedLine, FadeIn, Reveal } from "../components/Reveal";
import { Eyebrow, ArrowLink } from "../components/atoms";
import { MediaSlot } from "../components/MediaSlot";
import { ResourceList } from "../components/ResourceCard";
import { FeeTierTable } from "../components/FeeTiers";
import { MobileActionBar } from "../components/MobileActionBar";
import { SERVICES, WALKTHROUGHS, CALENDLY_URL, PAID_BY_YOU, VENDOR_FEE_CONTEXT, AGENT_SELECTION_NOTE } from "../data/content";

const WALK_KEY = {
  "buyer-advocacy": "buyer",
  "vendor-advocacy": "vendor",
  "property-portfolio-review": "review",
};

const MEDIA_KEY = {
  "buyer-advocacy": "buyer-brief",
  "vendor-advocacy": "vendor-plan",
  "property-portfolio-review": "review-includes",
};

const CANONICAL_PATH = {
  "buyer-advocacy": "/buyer-advocacy",
  "vendor-advocacy": "/vendor-advocacy",
  "property-portfolio-review": "/property-portfolio-assessment",
};

const CARD_LINE = {
  "buyer-advocacy": "Fixed professional fee, agreed at engagement — we do not earn more because the property costs more.",
  "vendor-advocacy": VENDOR_FEE_CONTEXT,
  "property-portfolio-review": "Fixed professional fee, agreed in writing before work begins.",
};

// Primary action shown in each service hero, adjacent to the fee — visitors
// never have to scroll to find the next step (owner direction 31 Oct 2026).
const HERO_CTA = {
  "buyer-advocacy": { label: "Book a Buyer Advocacy call", note: null },
  "vendor-advocacy": { label: "Book a Vendor Advocacy call", note: null },
  "property-portfolio-review": {
    label: "Book an assessment call",
    note: "A free 15-minute introductory call — the assessment itself is the paid engagement.",
  },
};

export default function ServicePage({ serviceKey }) {
  const s = SERVICES[serviceKey];
  if (!s) return <Navigate to="/" replace />;
  const walk = WALKTHROUGHS[WALK_KEY[serviceKey]];
  const mediaId = MEDIA_KEY[serviceKey];
  const isBuyer = serviceKey === "buyer-advocacy";
  const isVendor = serviceKey === "vendor-advocacy";

  return (
    <>
      <Seo
        title={`${s.name} — ${s.fee} | 80 Metrics`}
        description={s.intro}
        jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: s.name, path: CANONICAL_PATH[serviceKey] }])]}
      />
      <section className="bg-charcoal text-ivory" data-testid={`service-hero-${serviceKey}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-28 pb-14 lg:pb-20">
          <FadeIn delay={0.1}>
            <Eyebrow dark>{s.label} · {s.name}</Eyebrow>
          </FadeIn>
          <h1 className="mt-8 font-serif font-medium text-4xl sm:text-5xl lg:text-7xl leading-[1.04] tracking-tight max-w-4xl">
            <MaskedLine delay={0.25}>{s.headline}</MaskedLine>
          </h1>
          <FadeIn delay={0.75}>
            <p className="mt-8 text-sm lg:text-base leading-relaxed text-ivory/70 max-w-2xl" data-testid={`service-boundary-${serviceKey}`}>
              {s.boundary}
            </p>
            <p className="mt-6 font-serif text-xl text-goldlight">{s.fee}</p>
            {HERO_CTA[serviceKey] && (
              <div className="mt-8">
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`service-hero-cta-${serviceKey}`}
                  className="inline-flex items-center justify-center px-8 py-4 bg-gold text-ink text-sm font-semibold transition-[background-color] duration-300 hover:bg-ivory"
                >
                  {HERO_CTA[serviceKey].label}
                </a>
                {HERO_CTA[serviceKey].note && (
                  <p className="mt-3 text-xs leading-relaxed text-ivory/55 max-w-md" data-testid={`service-hero-cta-note-${serviceKey}`}>
                    {HERO_CTA[serviceKey].note}
                  </p>
                )}
              </div>
            )}
          </FadeIn>
        </div>
      </section>

      <section className="bg-white" data-testid={`service-body-${serviceKey}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <Reveal className="lg:col-span-7">
            <p className="font-serif text-2xl lg:text-3xl font-medium leading-snug text-charcoal">{s.intro}</p>
            {walk && (
              <div data-testid={`service-walkthrough-${serviceKey}`}>
                <h2 className="mt-14 text-xs font-semibold tracking-[0.3em] uppercase text-golddark">{walk.title}</h2>
                <ol className="mt-6 border-t border-charcoal/15">
                  {walk.steps.map((st, i) => (
                    <li key={st.name} className="py-5 border-b border-charcoal/15 grid grid-cols-12 gap-4" data-testid={`service-walkthrough-step-${i}`}>
                      <span className="col-span-2 sm:col-span-1 font-serif text-lg text-gold">0{i + 1}</span>
                      <div className="col-span-10 sm:col-span-11">
                        <p className="text-sm font-semibold text-charcoal">{st.name}</p>
                        <p className="mt-1 text-sm leading-relaxed text-charcoal/65">{st.body}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}
            {mediaId && <MediaSlot id={mediaId} className="mt-12" />}
            <h2 className="mt-14 text-xs font-semibold tracking-[0.3em] uppercase text-golddark">What’s included</h2>
            <ul className="mt-6 border-t border-charcoal/15">
              {s.points.map((p) => (
                <li key={p} className="py-5 border-b border-charcoal/15 text-sm lg:text-base text-charcoal/75 leading-relaxed flex gap-4">
                  <span className="mt-2.5 h-1.5 w-1.5 shrink-0 bg-gold rotate-45" />
                  {p}
                </li>
              ))}
            </ul>
            {isVendor && (
              <div className="mt-8 border border-gold/50 bg-bone px-5 py-4" data-testid="vendor-independence-note">
                <p className="text-xs leading-relaxed text-charcoal/75">{PAID_BY_YOU}</p>
                <p className="mt-2 text-xs leading-relaxed text-charcoal/75">{AGENT_SELECTION_NOTE}</p>
              </div>
            )}
            <p className="mt-10 text-sm text-charcoal/60 leading-relaxed max-w-xl">
              Advice does not have to lead to a transaction. Scope and fees are agreed in writing before any paid work begins.
            </p>
            <p className="mt-6 border border-gold/50 bg-bone px-5 py-4 text-xs leading-relaxed text-charcoal/75 max-w-xl" data-testid={`service-fallback-${serviceKey}`}>
              Not sure which service fits? Tell us what you’re trying to decide and we’ll point you to the right
              starting point — including telling you if you don’t need a paid service.
            </p>
            <ResourceList service={serviceKey} className="mt-10" />
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="bg-charcoal text-ivory p-8 lg:p-12 lg:sticky lg:top-28 relative lg:z-[70]" data-testid={`service-cta-card-${serviceKey}`}>
              <p className="text-xs font-semibold tracking-[0.28em] uppercase text-goldlight">{s.name}</p>
              <p className="mt-4 font-serif text-3xl font-medium">{s.fee}</p>
              <p className="mt-4 text-sm leading-relaxed text-ivory/60">
                {CARD_LINE[serviceKey]}
              </p>
              {isBuyer && (
                <div className="mt-6 border-t border-white/15 pt-5">
                  <FeeTierTable dark />
                </div>
              )}
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`service-book-${serviceKey}`}
                className="mt-8 w-full inline-flex items-center justify-center px-8 py-4 bg-gold text-ink text-xs font-semibold tracking-[0.2em] uppercase transition-[background-color] duration-300 hover:bg-ivory"
              >
                Book my 15-minute call
              </a>
              <p className="mt-4 text-xs leading-relaxed text-ivory/50">
                A short introductory call with Matthew to identify your next step. Scope and fees are agreed before any paid work begins.
              </p>
              <div className="mt-5 text-center">
                <Link
                  to={`/contact?service=${s.serviceKey}`}
                  data-testid={`service-cta-${serviceKey}`}
                  className="text-[11px] font-semibold tracking-[0.18em] uppercase text-ivory/70 hover:text-goldlight transition-colors duration-300"
                >
                  Prefer to write? Send an enquiry →
                </Link>
              </div>
              <div className="mt-4 text-center">
                <ArrowLink to="/how-we-work" dark data-testid={`service-hww-${serviceKey}`}>How the services fit together</ArrowLink>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <MobileActionBar serviceKey={serviceKey} watchTestId={`service-hero-cta-${serviceKey}`} />
    </>
  );
}
